import { SelectButton } from '@modules/queryBuilder/components/SelectButton';
import {
  MULTI_VALUE_OPERATORS,
  NO_VALUE_OPERATORS,
} from '@modules/queryBuilder/constants';
import {
  getFieldOperators,
  getFilterValueFromType,
  getFilterValueType,
  upsertQueryFilterNode,
} from '@modules/queryBuilder/helpers';
import { QueryBuilderActionType } from '@modules/queryBuilder/store/queryBuilderActionType';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { MenuItem, MenuList } from '@mui/material';
import { NormalisedFilter } from 'v2/querybuilder/types/querystore';

interface QueryFilterOperatorSelectButtonProps {
  filter: NormalisedFilter;
}

export function QueryFilterOperatorSelectButton({
  filter,
}: QueryFilterOperatorSelectButtonProps) {
  const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
  const nodes = useQueryBuilderStore(({ query }) => query.nodes);
  const eqlFieldByFieldID = useQueryBuilderStore(
    ({ metadata }) => metadata.eqlFieldByFieldID,
  );

  const eqlField = eqlFieldByFieldID[filter.field]?.field;

  if (!eqlField) {
    return null;
  }

  const filterValueType = getFilterValueType(eqlField);
  const operators = getFieldOperators({
    field: filter.field,
    filterValueType: filterValueType,
  });

  const onOperatorChange = (newOperator: string) => {
    if (filter.operator === newOperator) {
      return;
    }

    const isTypeEqual = [MULTI_VALUE_OPERATORS, NO_VALUE_OPERATORS].some(
      (operators) =>
        operators.includes(filter.operator) === operators.includes(newOperator),
    );

    let value = filter.value;
    if (!isTypeEqual && eqlField) {
      const type = NO_VALUE_OPERATORS.includes(newOperator)
        ? 'boolean'
        : filterValueType;

      value = getFilterValueFromType({ type });
    }

    const updatedNodes = upsertQueryFilterNode({
      node: { ...filter, value, operator: newOperator },
      nodesByID: nodes,
      parentID: filter.parent,
    });

    dispatch({
      type: QueryBuilderActionType.SET_QUERY,
      payload: {
        nodes: updatedNodes,
      },
    });
  };

  return (
    <SelectButton
      className="qb-filter-operator-select-button"
      shouldShowTooltip={false}
      label={filter.operator ?? ''}
      renderDropDownComponent={({ onClose }) => (
        <MenuList
          id={`qb-filter-operator-dropdown-${filter.id}`}
          dense
          autoFocusItem
          onKeyDown={(event) => {
            if (event.key === 'Tab') {
              event.preventDefault();
              onClose();
            }
          }}
        >
          {operators.map((operator) => {
            return (
              <MenuItem
                key={operator}
                value={operator}
                autoFocus={operator === filter.operator}
                onClick={() => {
                  onOperatorChange(operator);
                  onClose();
                }}
              >
                {operator}
              </MenuItem>
            );
          })}
        </MenuList>
      )}
    />
  );
}
